/**
 * Modal for creating a new map bundle.
 * Multi-step: Step 1 selects Area of Operation, Step 2 collects bundle details.
 */

import React, { useState, useCallback } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import { createBundle } from '../../store/mapBundleSlice';
import {
    AoOBounds,
    MapBundleStyle,
    MAP_BUNDLE_STYLE_OPTIONS,
} from '../../types/mapBundle';
import { Faction } from '../../types/orbat';
import { boundingBoxAreaKm2, formatArea, MAX_AOO_AREA_KM2 } from '../../utils/boundsArea';
import AoOMapSelector from './AoOMapSelector';
import './CreateBundleModal.scss';

interface CreateBundleModalProps {
    show: boolean;
    onHide: () => void;
    eventId: string;
}

const CreateBundleModal: React.FC<CreateBundleModalProps> = ({ show, onHide, eventId }) => {
    const dispatch = useDispatch<AppDispatch>();
    const { factions } = useSelector((state: RootState) => state.orbat);
    const { isCreating } = useSelector((state: RootState) => state.mapBundle);

    const [step, setStep] = useState<1 | 2>(1);
    const [bounds, setBounds] = useState<AoOBounds | null>(null);
    const [bundleName, setBundleName] = useState('');
    const [nameError, setNameError] = useState<string | null>(null);
    const [selectedFactionId, setSelectedFactionId] = useState<string | null>(null);
    const [selectedStyle, setSelectedStyle] = useState<MapBundleStyle>('topo-v2');

    const handleBoundsChange = useCallback((newBounds: AoOBounds | null) => {
        setBounds(newBounds);
    }, []);

    const handleReset = useCallback(() => {
        setBounds(null);
    }, []);

    const handleNext = useCallback(() => {
        if (bounds) {
            setStep(2);
        }
    }, [bounds]);

    const handleBack = useCallback(() => {
        setStep(1);
        setNameError(null);
    }, []);

    const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setBundleName(e.target.value);
        if (nameError) setNameError(null);
    }, [nameError]);

    const handleFactionChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
        const value = e.target.value;
        setSelectedFactionId(value === '' ? null : value);
    }, []);

    const handleStyleChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedStyle(e.target.value as MapBundleStyle);
    }, []);

    const handleCreate = useCallback(async () => {
        if (!bounds) return;

        const trimmedName = bundleName.trim();
        if (!trimmedName) {
            setNameError('Bundle name is required.');
            return;
        }

        const result = await dispatch(
            createBundle({
                eventId,
                payload: {
                    name: trimmedName,
                    faction_id: selectedFactionId,
                    style: selectedStyle,
                    ...bounds,
                },
            }),
        );
        if (createBundle.fulfilled.match(result)) {
            // Reset local state and close modal
            setStep(1);
            setBounds(null);
            setBundleName('');
            setNameError(null);
            setSelectedFactionId(null);
            setSelectedStyle('topo-v2');
            onHide();
        }
    }, [bounds, bundleName, selectedFactionId, selectedStyle, dispatch, eventId, onHide]);

    const handleClose = useCallback(() => {
        setStep(1);
        setBounds(null);
        setBundleName('');
        setNameError(null);
        setSelectedFactionId(null);
        setSelectedStyle('topo-v2');
        onHide();
    }, [onHide]);

    const areaKm2 = bounds ? boundingBoxAreaKm2(bounds) : 0;
    const isAreaTooLarge = bounds !== null && areaKm2 > MAX_AOO_AREA_KM2;

    return (
        <Modal
            show={show}
            onHide={handleClose}
            size="xl"
            className="create-bundle-modal"
            backdrop="static"
        >
            <Modal.Header closeButton>
                <Modal.Title>Create Map Bundle</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {/* Step indicator */}
                <div className="step-indicator">
                    <span className={`step-number ${step === 1 ? 'active' : 'completed'}`}>1</span>
                    <span className={`step-label ${step === 1 ? 'active' : ''}`}>
                        Area of Operation
                    </span>
                    <span className={`step-line ${step === 2 ? 'completed' : ''}`} />
                    <span className={`step-number ${step === 2 ? 'active' : 'inactive'}`}>2</span>
                    <span className={`step-label ${step === 2 ? 'active' : ''}`}>
                        Bundle Details
                    </span>
                </div>

                {step === 1 && (
                    <>
                        <p className="text-muted mb-2">
                            Click on the map to set the first corner of the Area of Operation,
                            then click again to set the opposite corner.
                        </p>
                        <AoOMapSelector bounds={bounds} onBoundsChange={handleBoundsChange} />
                        {bounds && (
                            <div className="bounds-display mt-3">
                                <span className={isAreaTooLarge ? 'text-danger' : undefined}>
                                    Area: {formatArea(areaKm2)}
                                </span>
                                {isAreaTooLarge && (
                                    <div className="text-danger small mt-1">
                                        The selected area exceeds the {MAX_AOO_AREA_KM2} km²
                                        maximum. Reduce the area to continue.
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}

                {step === 2 && (
                    <>
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label>Bundle Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="e.g. Main AO Map"
                                    value={bundleName}
                                    onChange={handleNameChange}
                                    isInvalid={!!nameError}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {nameError}
                                </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Faction Access</Form.Label>
                                <Form.Select
                                    value={selectedFactionId ?? ''}
                                    onChange={handleFactionChange}
                                >
                                    <option value="">All factions</option>
                                    {factions.map((faction: Faction) => (
                                        <option key={faction.id} value={faction.id}>
                                            {faction.name}
                                        </option>
                                    ))}
                                </Form.Select>
                                <Form.Text className="text-muted">
                                    Choose a faction to restrict access, or leave as
                                    &quot;All factions&quot; to make it available to everyone.
                                </Form.Text>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Map Style</Form.Label>
                                <Form.Select
                                    value={selectedStyle}
                                    onChange={handleStyleChange}
                                >
                                    {MAP_BUNDLE_STYLE_OPTIONS.map((opt) => (
                                        <option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </Form.Select>
                                <Form.Text className="text-muted">
                                    {
                                        MAP_BUNDLE_STYLE_OPTIONS.find(
                                            (o) => o.value === selectedStyle,
                                        )?.description
                                    }
                                </Form.Text>
                            </Form.Group>
                        </Form>

                        {bounds && (
                            <div className="bounds-display mt-2">
                                <span className={isAreaTooLarge ? 'text-danger' : undefined}>
                                    Selected area: {formatArea(areaKm2)}
                                </span>
                                {isAreaTooLarge && (
                                    <div className="text-danger small mt-1">
                                        The selected area exceeds the {MAX_AOO_AREA_KM2} km²
                                        maximum. Go back and reduce the area to continue.
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </Modal.Body>

            <Modal.Footer>
                {step === 1 && (
                    <>
                        <Button variant="outline-secondary" onClick={handleReset} disabled={!bounds}>
                            Reset Selection
                        </Button>
                        <Button variant="secondary" onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleNext}
                            disabled={!bounds || isAreaTooLarge}
                        >
                            Next
                        </Button>
                    </>
                )}
                {step === 2 && (
                    <>
                        <Button variant="secondary" onClick={handleBack} disabled={isCreating}>
                            Back
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleCreate}
                            disabled={isCreating || !bounds || isAreaTooLarge}
                        >
                            {isCreating ? (
                                <>
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                        className="me-2"
                                    />
                                    Creating...
                                </>
                            ) : (
                                'Create'
                            )}
                        </Button>
                    </>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default CreateBundleModal;
